/**
 * Global Reusable Functions for RS Camp Pages
 */
window.RsCampPage = window.RsCampPage || {};

window.RsCampPage.setCookie = function(formData) {
  const cookieName = 'rs_camp_form_data';
  const cookieValue = JSON.stringify(formData);
  const expirationDate = new Date();
  expirationDate.setDate(expirationDate.getDate() + 7);

  document.cookie = `${cookieName}=${encodeURIComponent(cookieValue)}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax`;
};

window.RsCampPage.getCookie = function() {
  const cookieName = 'rs_camp_form_data';
  const cookies = document.cookie.split(';');
  
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === cookieName) {
      try {
        return JSON.parse(decodeURIComponent(value));
      } catch (error) {
        console.error('Error parsing form data cookie:', error);
        return null;
      }
    }
  }
  return null;
};

window.RsCampPage.clearCookie = function() {
  const cookieName = 'rs_camp_form_data';
  document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax`;
};

/**
 * Format stored height (5x11 or 5 x 11) for display as 5' 11"
 */
window.RsCampPage.formatHeightForDisplay = function(height) {
  if (!height || typeof height !== 'string') return height || '';
  const match = height.replace(/\s/g, '').match(/^(\d+)x(\d+)$/i);
  return match ? `${match[1]}' ${match[2]}"` : height;
};

window.RsCampPage.getCampData = function() {
  if (window.RsCampPage._campData) {
    return window.RsCampPage._campData;
  }

  const campDataScript = document.querySelector('script[data-camp-page-data]');
  if (campDataScript) {
    try {
      window.RsCampPage._campData = JSON.parse(campDataScript.textContent);
      return window.RsCampPage._campData;
    } catch (error) {
      console.error('Error parsing camp data:', error);
      return null;
    }
  }
  
  return null;
};

/**
 * Scroll to rs-camp-result with header offset
 */
window.RsCampPage.scrollToResult = function() {
  const resultContainer = document.querySelector('rs-camp-result');
  if (!resultContainer) return;

  const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 120;
  const elementTop = resultContainer.getBoundingClientRect().top + window.pageYOffset;
  const offsetPosition = elementTop - headerHeight - 20;

  window.scrollTo({
    top: offsetPosition,
    behavior: 'smooth'
  });
};

/**
 * Global function to load products using Shopify Section Rendering API
 * @param {string} collectionUrl - The collection URL
 * @param {string} filterParams - Filter query parameters
 * @param {HTMLElement} container - Container element to inject HTML
 * @param {boolean} enableFiltering - Whether filtering is enabled
 */
window.RsCampPage.loadProducts = async function(collectionUrl, filterParams = '', container, enableFiltering = false, enableDepartmentFiltering = false) {
  container.classList.add('is-loading');
  
  try {
    const campData = window.RsCampPage.getCampData();
    const sectionId = campData.collection_section;
    
    let url = `${collectionUrl}?section_id=${sectionId}`;
    
    /**
     * APPLY DEPARTMENT FILTER BY DEFAULT WHEN
     *  - Filtering and department filtering are enabled
     *  - The department filter is not already in the URL
     */
    const formData = window.RsCampPage.getCookie();
    if (formData && formData.campDepartment && enableFiltering && enableDepartmentFiltering) {
      if (!url.includes('filter.p.m.custom.dept=') && !filterParams.includes('filter.p.m.custom.dept=')) {
        const departmentFilter = `filter.p.m.custom.dept=${formData.campDepartment}`;
        url += `&${departmentFilter}`;
      }
    }
    
    if (filterParams) {
      url += `&${filterParams}`;
    }
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const html = await response.text();
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    const sectionContent = tempDiv.querySelector('#shopify-section-rs-camp-page-collection');
    if (sectionContent) {
      if (enableFiltering) {
        container.innerHTML = sectionContent.innerHTML; // Both filters and products
      } else {
        const productsElement = sectionContent.querySelector('rs-camp-result-products');
        container.innerHTML = productsElement.outerHTML; // Only products
      }
    }

  } catch (error) {
    console.error('Error loading products:', error);
    container.innerHTML = '<p>Error loading products. Please try again.</p>';
  } finally {
    container.classList.remove('is-loading');
  }
};


/**
 * RS Camp Form. Handles form submission:
 *  - Cookie storage using HTML5 validation
 *  - Show/hide form and result sections based on form submission
 */
class RSCampForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('[data-camp-form]');
    this.boundSubmitHandler = this.handleSubmit.bind(this);
  }

  connectedCallback() {
    if (this.form) {
      this.form.addEventListener('submit', this.boundSubmitHandler);
    }
    this.initializePage();
  }

  /**
   * On page load - check for existing cookie and show appropriate step
   */
  initializePage() {
    const existingData = window.RsCampPage.getCookie();
    if (existingData) {
      this.toggleSections(true);
      this.dispatchFormEvent(existingData);
    } else {
      this.toggleSections(false);
    }
  }

  disconnectedCallback() {
    if (this.form) {
      this.form.removeEventListener('submit', this.boundSubmitHandler);
    }
  }

  /**
   * Handle form submission
   */
  handleSubmit(event) {
    event.preventDefault();
    
    if (this.form.checkValidity()) {
      const formData = this.collectFormData();
      window.RsCampPage.setCookie(formData);
      this.toggleSections(true);
      this.dispatchFormEvent(formData);
    } else {
      this.form.reportValidity();
    }
  }

  /**
   * Collect form data from FormData API
   */
  collectFormData() {
    const formData = new FormData(this.form);
    const feet = formData.get('camper-height-feet') || '';
    const inches = formData.get('camper-height-inches') || '';
    const camperHeight = (feet && inches) ? `${feet} x ${inches}` : '';

    return {
      camperName: formData.get('camper-name') || '',
      campNameLabel: formData.get('camper-name-label') || '',
      camperAge: parseInt(formData.get('camper-age')) || 0,
      camperHeight,
      camperWeight: parseInt(formData.get('camper-weight')) || 0,
      campDepartment: formData.get('camp-department') || '',
      timestamp: new Date().toISOString()
    };
  }


  /**
   * Toggle between form and result sections
   */
  toggleSections(showResult = true) {
    const formWrapper = this.closest('rs-camp-form-wrapper');
    const resultWrapper = document.querySelector('rs-camp-result-wrapper');
    const mainContent = document.querySelector('#MainContent');
    
    // Toggle form and result sections
    if (formWrapper && resultWrapper) {
      if (showResult) {
        formWrapper.setAttribute('hidden', '');
        resultWrapper.removeAttribute('hidden');
      } else {
        formWrapper.removeAttribute('hidden');
        resultWrapper.setAttribute('hidden', '');
      }
    }

    // Toggle all other sections below main camp page section
    if (mainContent) {
      mainContent.querySelectorAll('.shopify-section').forEach(section => {
        if (!section.classList.contains('shopify-section--camp-page')) {
          section.toggleAttribute('hidden', !showResult);
        }
      });
    }
  }

  /**
   * Dispatch form submitted event
   */
  dispatchFormEvent(formData) {
    document.dispatchEvent(new CustomEvent('rs:camp-form:submitted', {
      bubbles: true,
      detail: { formData }
    }));
  }
}


/**
 * RS Camp Result. Handles result section:
 *  - Generates sidebar and content based on customization settings
 *  - Handles sidebar navigation and content display
 */
class RSCampResult extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
  }

  connectedCallback() {
    this.boundHandleFormSubmitted = this.handleFormSubmitted.bind(this);
    this.boundHandleQuickAddModalCreated = this.handleQuickAddModalCreated.bind(this);
    
    document.addEventListener('rs:camp-form:submitted', this.boundHandleFormSubmitted);
    document.addEventListener('theme:quick-add:modal-created', this.boundHandleQuickAddModalCreated);
    this.initializeResult();
  }

  disconnectedCallback() {
    if (this.boundHandleFormSubmitted) {
      document.removeEventListener('rs:camp-form:submitted', this.boundHandleFormSubmitted);
    }
    if (this.boundHandleQuickAddModalCreated) {
      document.removeEventListener('theme:quick-add:modal-created', this.boundHandleQuickAddModalCreated);
    }
  }

  /**
   * Initialize result section
   */
  initializeResult() {
    const formData = window.RsCampPage.getCookie();
    if (formData) {
      this.handleFormSubmitted();
    }
  }

  /**
   * Handle form submitted event and build sidebar
   */
  handleFormSubmitted() {
    const campData = window.RsCampPage.getCampData();
    if (!campData || !campData.checklist_blocks) return;

    const accordionContainer = this.querySelector('[data-camp-result-accordion]');
    const dropdownContainer = this.querySelector('[data-camp-result-dropdown] [data-grid-slider]');
    const dropdownWrapper = this.querySelector('[data-camp-result-dropdown]');
    
    if (accordionContainer) {
      accordionContainer.innerHTML = this.generateAccordionHTML();
      
      this.querySelectorAll('.camp-result__nav-button').forEach(button => {
        button.addEventListener('click', async (e) => {
          if (accordionContainer && accordionContainer.classList.contains('is-disabled')) {
            return;
          }
          
          // Prevent duplicate requests if button is already active
          if (e.target.classList.contains('is-active')) {
            return;
          }
          
          accordionContainer.classList.add('is-disabled');
          
          try {
            const index = parseInt(e.target.getAttribute('data-item-index'));
            window.RsCampPage.scrollToResult();
            await this.showContentByIndex(index);           
          } finally {
            accordionContainer.classList.remove('is-disabled');
          }
        });
      });
    }

    if (dropdownContainer) {
      dropdownContainer.innerHTML = this.generateDropdownHTML();
      
        requestAnimationFrame(() => {
          const gridSlider = this.querySelector('.camp-result__dropdown-slider');
          if (gridSlider) {
            window.dispatchEvent(new Event('resize'));
            
            if (typeof gridSlider.init === 'function') {
              gridSlider.init();
            }
            
            // Add event listener for slider arrow clicks to close dropdowns
            this.addSliderArrowListeners(gridSlider);
          }
        });

        this.querySelectorAll('.camp-result__dropdown-item button[data-item-index]').forEach(button => {
          button.addEventListener('click', async (e) => {
            if (dropdownWrapper && dropdownWrapper.classList.contains('is-disabled')) {
              return;
            }
            
            // Prevent duplicate requests if the specific button is already active
            if (e.target.classList.contains('is-active')) {
              // Still close dropdown and scroll for UX, but don't fetch content
              const dropdownItem = e.target.closest('.camp-result__dropdown-item');
              if (dropdownItem && dropdownItem.getAttribute('data-content-type') === 'products') {
                const details = e.target.closest('details');
                if (details) {
                  details.removeAttribute('open');
                }
              }
              this.scrollToContentOnMobile();
              return;
            }
            
            dropdownWrapper.classList.add('is-disabled');
            
            try {
              const index = parseInt(e.target.getAttribute('data-item-index'));
              await this.showContentByIndex(index);
              
              const dropdownItem = e.target.closest('.camp-result__dropdown-item');
              if (dropdownItem && dropdownItem.getAttribute('data-content-type') === 'products') {
                const details = e.target.closest('details');
                if (details) {
                  details.removeAttribute('open');
                }
              }
              
              // Scroll to content area on mobile
              this.scrollToContentOnMobile();
            } finally {
              dropdownWrapper.classList.remove('is-disabled');
            }
          });
        });
    }
    
    // Show first item by default
    this.showContentByIndex(1);

    // Show shopper info
    this.showShopperInfo();
  }

  /**
   * Show shopper info with reset functionality
   */
  showShopperInfo() {
    const shopperInfoContainer = document.querySelector('rs-camp-shopper-info');
    if (!shopperInfoContainer) return;

    const formData = window.RsCampPage.getCookie();
    const campData = window.RsCampPage.getCampData();
    if (!formData) return;

    shopperInfoContainer.innerHTML = `
      <div class="camp-result__shopper--title h3 heading-small">${campData.camp_name}</div>
      <div class="camp-result__shopper--message">
        <div class="camp-result__shopper--message-name">
          You're currently shopping for <strong>${formData.camperName}</strong>
        </div>
        <div class="camp-result__shopper--message-info">
          <span><strong>${formData.camperAge}</strong> ${formData.camperAge === 1 ? 'year' : 'years'} old</span>
          <span><strong>${window.RsCampPage.formatHeightForDisplay(formData.camperHeight)}</strong> tall</span>
          <span><strong>${formData.camperWeight}</strong> lbs</span>
        </div>
      </div>
      <div class="camp-result__shopper--info">
        Note: Changing the camper will clear your current camper details but will not affect your cart.
      </div>
      <div class="camp-result__shopper--button">
        <button type="button" class="btn btn--small btn--outline btn--secondary" data-shopper-info-reset>
          Change to another camper
        </button>
      </div> 
    `;
    
    // Add reset functionality
    shopperInfoContainer.querySelector('[data-shopper-info-reset]').addEventListener('click', () => {
      window.RsCampPage.clearCookie();
      window.location.reload();
    });
  }

  /**
   * Show content based on item index
   */
  async showContentByIndex(index) {
    const button = this.querySelector(`[data-item-index="${index}"]`);
    const contentContainer = this.querySelector('[data-camp-result-content]');
    if (!button || !contentContainer) return;

    // Update current index and active state
    this.currentIndex = index;
    
    // Update active state for desktop accordion buttons
    this.querySelectorAll('.camp-result__nav-button').forEach(btn => btn.classList.remove('is-active'));
    button.classList.add('is-active');
    
    // Update active state for mobile dropdown items
    this.resetDropdownTitles();
    
    this.querySelectorAll('.camp-result__dropdown-item').forEach(item => {
      item.classList.remove('is-active');
      const contentType = item.getAttribute('data-content-type');
      
      if (contentType === 'text' || contentType === 'featured_products') {
        const button = item.querySelector('button[data-item-index]');
        if (button && button.getAttribute('data-item-index') == index) {
          item.classList.add('is-active');
        }
      } else if (contentType === 'products') {
        const allButtons = item.querySelectorAll('.camp-result__dropdown-button');
        allButtons.forEach(btn => btn.classList.remove('is-active'));
        
        const matchingButton = item.querySelector(`[data-item-index="${index}"]`);
        if (matchingButton) {
          item.classList.add('is-active');
          matchingButton.classList.add('is-active');
          
          const summary = item.querySelector('.camp-result__dropdown-summary .camp-result__dropdown-label');
          if (summary) {
            summary.textContent = matchingButton.textContent;
          }
        }
      }
    });

    // Get content data from JSON using indices
    const contentData = this.getContentDataByButton(button);
    if (!contentData) return;

    // Render content based on type
    if (contentData.type === 'text') {
      contentContainer.innerHTML = `
        <h3 class="camp-result__content-title heading-small">${contentData.blockTitle}</h3>
        <div class="camp-result__content-text">${contentData.text}</div>
      `;
    } else if (contentData.type === 'featured_products') {
      contentContainer.innerHTML = `
        <h3 class="camp-result__content-title heading-small">${contentData.blockTitle}</h3>
        <div class="camp-result__content-featured-products is-loading"></div>
      `;
      
      // Load featured products using URL fetching
      await this.loadFeaturedProducts(contentContainer.querySelector('.camp-result__content-featured-products'), contentData);
    } else {
      contentContainer.innerHTML = `
        <h3 class="camp-result__content-title heading-small">${contentData.blockTitle}</h3>
        <h4 class="camp-result__content-subtitle">Select ${contentData.itemTitle}</h4>
        <div class="camp-result__content-products is-loading" data-enable-dept-filtering="${contentData.enableDepartmentFiltering || false}"></div>
      `;
      
      // Load products using Section Rendering API
      await this.loadProducts(contentData, contentContainer.querySelector('.camp-result__content-products'));
    }
  }

  /**
   * Get content data from JSON using button indices
   */
  getContentDataByButton(button) {
    const blockIndex = parseInt(button.getAttribute('data-block-index'));
    const itemSubIndex = parseInt(button.getAttribute('data-item-sub-index'));
    const campData = window.RsCampPage.getCampData();
    const block = campData.checklist_blocks.find(b => b.index === blockIndex);

    if (!block) return null;

    if (block.checklist_content_type === 'text') {
      return {
        type: 'text',
        blockTitle: block.checklist_title,
        text: block.checklist_text
      };
    } else if (block.checklist_content_type === 'featured_products') {
      return {
        type: 'featured_products',
        blockTitle: block.checklist_title
      };
    } else {
      const item = block.checklist_items.find(i => i.index === itemSubIndex);
      if (!item) return null;

      return {
        type: 'products',
        blockTitle: block.checklist_title,
        itemTitle: item.title,
        collectionUrl: item.collection,
        enableFiltering: item.enable_filtering,
        enableDepartmentFiltering: item.enable_department_filtering
      };
    }
  }

  /**
   * Load products using global loadProducts function
   */
  async loadProducts(contentData, container) {
    await window.RsCampPage.loadProducts(
      contentData.collectionUrl,
      '', // No filter params for initial load
      container,
      contentData.enableFiltering,
      contentData.enableDepartmentFiltering
    );

    // Initialize line item properties for dynamically loaded products
    this.initializeDynamicProducts(container, contentData);
  }

  /**
   * Load featured products by fetching URL and extracting main-content
   */
  async loadFeaturedProducts(container, contentData) {
    container.classList.add('is-loading');
    
    try {
      const url = `${location.pathname}?view=rs-camp-featured-products`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const html = await response.text();
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      
      // Try multiple selectors to find the main content
      let mainContent = tempDiv.querySelector('#MainContent') || tempDiv.querySelector('.main-content');
      
        if (mainContent) {
          container.innerHTML = mainContent.innerHTML;
          
          // Add line item properties to featured products
          this.initializeFeaturedProducts(container, contentData);
        } else {
          throw new Error('main content area not found in response');
        }
      
    } catch (error) {
      console.error('Error loading featured products:', error);
      container.innerHTML = '<p>Something went wrong. Please try again.</p>';
    } finally {
      container.classList.remove('is-loading');
    }
  }

  /**
   * Initialize line item properties for dynamically loaded products
   */
  initializeDynamicProducts(container, contentData) {
    this.addLineItemPropertiesToDirectAddToCartForms(container, contentData);
    this.addLineItemPropertiesToQuickAddButtons(container, contentData);
  }

  /**
   * Initialize line item properties for featured products
   */
  initializeFeaturedProducts(container, contentData) {
    this.addLineItemPropertiesToFeaturedProducts(container, contentData);
    this.prefillFeaturedProductsNameInputs(container);
  }

  /**
   * Add line item properties to direct add-to-cart forms (single variant products)
   */
  addLineItemPropertiesToDirectAddToCartForms(container, contentData) {
    const directAddToCartForms = container.querySelectorAll('form[action*="/cart/add"]');
    directAddToCartForms.forEach(form => {
      this.addLineItemPropertiesInputs(form, contentData);
    });
  }

  /**
   * Add line item properties data attributes to quick-add buttons (multi-variant products)
   */
  addLineItemPropertiesToQuickAddButtons(container, contentData) {
    const quickAddButtons = container.querySelectorAll('[data-quick-add-btn]');
    
    quickAddButtons.forEach(button => {
      const campData = window.RsCampPage.getCampData();
      const formData = window.RsCampPage.getCookie();
      
      if (campData && formData) {
        button.setAttribute('data-camp-title', campData.camp_name);
        button.setAttribute('data-camp-category', contentData.itemTitle);
        button.setAttribute('data-camper-name', formData.camperName);
        button.setAttribute('data-camper-age', formData.camperAge);
        button.setAttribute('data-camper-height', formData.camperHeight);
        button.setAttribute('data-camper-weight', formData.camperWeight);

        if (formData.campNameLabel && formData.campNameLabel.trim()) {
          button.setAttribute('data-camp-name-label', formData.campNameLabel);
        }
      }
    });
  }

  /**
   * Add line item properties inputs to product form
   */
  addLineItemPropertiesInputs(form, contentData) {
    const campData = window.RsCampPage.getCampData();
    const formData = window.RsCampPage.getCookie();
    
    if (!campData || !formData) return;

    // Prevent duplicate inputs
    if (form.querySelector('input[name="properties[_CampTitle]"]')) return;

    const properties = [
      { name: 'properties[_CampTitle]', value: campData.camp_name },
      { name: 'properties[_CampCategory]', value: contentData.itemTitle },
      { name: 'properties[_CamperName]', value: formData.camperName },
      { name: 'properties[_CamperAge]', value: formData.camperAge },
      { name: 'properties[_CamperHeight]', value: formData.camperHeight },
      { name: 'properties[_CamperWeight]', value: formData.camperWeight }
    ];

    if (formData.campNameLabel && formData.campNameLabel.trim()) {
      properties.push({ name: 'properties[_CampNameLabel]', value: formData.campNameLabel });
    }

    properties.forEach(({ name, value }) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });
  }

  /**
   * Add line item properties to direct add-to-cart forms in featured products
   */
  addLineItemPropertiesToFeaturedProducts(container, contentData) {
    const directAddToCartForms = container.querySelectorAll('form[action*="/cart/add"]');
    directAddToCartForms.forEach(form => {
      this.addLineItemPropertiesInputsForFeaturedProducts(form, contentData);
    });
  }


  /**
   * Add line item properties inputs to featured product form
   */
  addLineItemPropertiesInputsForFeaturedProducts(form, contentData) {
    const campData = window.RsCampPage.getCampData();
    const formData = window.RsCampPage.getCookie();
    
    if (!campData || !formData || !contentData) return;

    // Prevent duplicate inputs
    if (form.querySelector('input[name="properties[_CampTitle]"]')) return;

    const properties = [
      { name: 'properties[_CampTitle]', value: campData.camp_name },
      { name: 'properties[_CampCategory]', value: contentData.blockTitle },
      { name: 'properties[_CamperName]', value: formData.camperName },
      { name: 'properties[_CamperAge]', value: formData.camperAge },
      { name: 'properties[_CamperHeight]', value: formData.camperHeight },
      { name: 'properties[_CamperWeight]', value: formData.camperWeight }
    ];

    if (formData.campNameLabel && formData.campNameLabel.trim()) {
      properties.push({ name: 'properties[_CampNameLabel]', value: formData.campNameLabel });
    }

    properties.forEach(({ name, value }) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });
  }

  /**
   * Prefill name inputs in featured products with camper's name from cookie
   */
  prefillFeaturedProductsNameInputs(container) {
    const formData = window.RsCampPage.getCookie();
    
    if (!formData || !formData.camperName) return;

    const nameInputs = container.querySelectorAll('.properties__input[name="properties[Name]"]');
    nameInputs.forEach(input => {
      if (input && !input.value) {
        input.value = formData.camperName;
      }
    });
  }

  /**
   * Handle quick-add modal created event (for multi-variant products)
   */
  handleQuickAddModalCreated(event) {
    const { modal, triggerButton } = event.detail;
    
    if (!triggerButton) return;
    
    // Get line item properties from button attributes
    const campTitle = triggerButton.getAttribute('data-camp-title');
    const campCategory = triggerButton.getAttribute('data-camp-category');
    const camperName = triggerButton.getAttribute('data-camper-name');
    const campNameLabel = triggerButton.getAttribute('data-camp-name-label') || '';
    const camperAge = triggerButton.getAttribute('data-camper-age');
    const camperHeight = triggerButton.getAttribute('data-camper-height');
    const camperWeight = triggerButton.getAttribute('data-camper-weight');
    
    if (!campTitle || !campCategory || !camperName) return;
    
    // Add line item properties to all forms in the modal
    this.addLineItemPropertiesToModalForms(modal, campTitle, campCategory, camperName, campNameLabel, camperAge, camperHeight, camperWeight);
  }

  /**
   * Add line item properties to all forms in a quick-add modal
   */
  addLineItemPropertiesToModalForms(modal, campTitle, campCategory, camperName, campNameLabel, camperAge, camperHeight, camperWeight) {
    const modalForms = modal.querySelectorAll('form[action*="/cart/add"]');
    
    modalForms.forEach(form => {
      // Prevent duplicate inputs
      if (form.querySelector('input[name="properties[_CampTitle]"]')) return;

      const properties = [
        { name: 'properties[_CampTitle]', value: campTitle },
        { name: 'properties[_CampCategory]', value: campCategory },
        { name: 'properties[_CamperName]', value: camperName },
        { name: 'properties[_CamperAge]', value: camperAge },
        { name: 'properties[_CamperHeight]', value: camperHeight },
        { name: 'properties[_CamperWeight]', value: camperWeight }
      ];
      
      if (campNameLabel && campNameLabel.trim()) {
        properties.push({ name: 'properties[_CampNameLabel]', value: campNameLabel });
      }

      properties.forEach(({ name, value }) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      });
    });
  }

  /**
   * Add event listeners to slider arrows to close open dropdowns
   */
  addSliderArrowListeners(gridSlider) {
    const arrowButtons = gridSlider.querySelectorAll('[data-button-arrow]');
    arrowButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.closeAllDropdowns();
      });
    });
  }

  /**
   * Close all open dropdowns
   */
  closeAllDropdowns() {
    const openDropdowns = this.querySelectorAll('.camp-result__dropdown-details[open]');
    openDropdowns.forEach(dropdown => {
      dropdown.removeAttribute('open');
    });
  }

  /**
   * Reset all dropdown titles to their original state
   */
  resetDropdownTitles() {
    const dropdownLabels = this.querySelectorAll('.camp-result__dropdown-label[data-original-title]');
    dropdownLabels.forEach(label => {
      const originalTitle = label.getAttribute('data-original-title');
      if (originalTitle) {
        label.textContent = originalTitle;
      }
    });
  }

  /**
   * Scroll to content area on mobile devices
   */
  scrollToContentOnMobile() {
    // Only scroll on mobile devices
    if (window.innerWidth >= 750) return;
    
    const contentArea = this.querySelector('[data-camp-result-content]');
    if (!contentArea) return;
    
    // Calculate scroll position with header offset
    const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 120;
    const elementTop = contentArea.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementTop - headerHeight - 60;
    
    // Smooth scroll to content
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }

  /**
   * Generate dropdown HTML for mobile slider navigation
   */
  generateDropdownHTML() {
    const campData = window.RsCampPage.getCampData();
    const blocks = campData.checklist_blocks;
    const dropdownItems = [];
    let globalIndex = 1;
    
    blocks.forEach((block, blockIndex) => {
      if (block.checklist_content_type === 'text') {
        dropdownItems.push(`
          <div class="camp-result__dropdown-item grid-item" data-grid-item data-content-type="text">
            <div class="camp-result__dropdown-content">
              <button type="button" class="btn btn--full" data-item-index="${globalIndex}" data-block-index="${block.index}">
                ${block.checklist_title}
              </button>
            </div>
          </div>
        `);
        globalIndex++;
      } else if (block.checklist_content_type === 'featured_products') {
        dropdownItems.push(`
          <div class="camp-result__dropdown-item grid-item" data-grid-item data-content-type="featured_products">
            <div class="camp-result__dropdown-content">
              <button type="button" class="btn btn--full" data-item-index="${globalIndex}" data-block-index="${block.index}">
                ${block.checklist_title}
              </button>
            </div>
          </div>
        `);
        globalIndex++;
      } else {
        const buttonsHTML = block.checklist_items.map(item => {
          const buttonHTML = `
            <button type="button" class="camp-result__dropdown-button btn btn--outline btn--small" data-item-index="${globalIndex}" data-block-index="${block.index}" data-item-sub-index="${item.index}">
              ${item.title}
            </button>
          `;
          globalIndex++;
          return buttonHTML;
        }).join('');

        dropdownItems.push(`
          <div class="camp-result__dropdown-item grid-item" data-grid-item data-content-type="products">
            <details class="camp-result__dropdown-details" data-collapsible>
              <summary class="camp-result__dropdown-summary" data-collapsible-trigger>
                <span class="camp-result__dropdown-label" data-original-title="${block.checklist_title}">${block.checklist_title}</span>
                <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-plus" viewBox="0 0 24 24"><path d="M6 12h6m6 0h-6m0 0V6m0 6v6" stroke="#000" stroke-linecap="round" stroke-linejoin="round"/></svg>
                <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-minus" viewBox="0 0 24 24"><path d="M6 12h12" stroke="#000" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </summary>
              <div class="camp-result__dropdown-body" data-collapsible-body>
                <div class="camp-result__dropdown-content" data-collapsible-content>
                  ${buttonsHTML}
                </div>
              </div>
            </details>
          </div>
        `);
      }
    });
    
    return dropdownItems.join('');
  }

  /**
   * Generate accordion HTML for desktop sidebar
   */
  generateAccordionHTML() {
    const campData = window.RsCampPage.getCampData();
    const blocks = campData.checklist_blocks;
    let globalIndex = 1;
    
    const sidebarHTML = blocks.map((block, blockIndex) => {
      if (block.checklist_content_type === 'text') {
        const buttonHTML = `
          <button type="button" class="camp-result__nav-button" data-item-index="${globalIndex}" data-block-index="${block.index}">
            ${block.checklist_title}
          </button>
        `;
        globalIndex++;
        return buttonHTML;
      } else if (block.checklist_content_type === 'featured_products') {
        const buttonHTML = `
          <button type="button" class="camp-result__nav-button" data-item-index="${globalIndex}" data-block-index="${block.index}">
            ${block.checklist_title}
          </button>
        `;
        globalIndex++;
        return buttonHTML;
      } else {
        const isFirst = blockIndex === 0;
        const openAttribute = isFirst ? 'open="true"' : '';
        const openStyle = isFirst ? 'style="height: auto;"' : '';

        const itemsHTML = block.checklist_items.map(item => {
          const buttonHTML = `
            <button type="button" class="camp-result__nav-button" data-item-index="${globalIndex}" data-block-index="${block.index}" data-item-sub-index="${item.index}">
              ${item.title}
            </button>
          `;
          globalIndex++;
          return buttonHTML;
        }).join('');

        return `
          <details class="camp-result__accordion" data-collapsible ${openAttribute}>
            <summary class="camp-result__accordion-title" data-collapsible-trigger>
              <span>${block.checklist_title}</span>
              <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-plus" viewBox="0 0 24 24"><path d="M6 12h6m6 0h-6m0 0V6m0 6v6" stroke="#000" stroke-linecap="round" stroke-linejoin="round"/></svg>
              <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-minus" viewBox="0 0 24 24"><path d="M6 12h12" stroke="#000" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </summary>
            <div class="camp-result__accordion-body" data-collapsible-body ${openStyle}>
              <div class="camp-result__accordion-content" data-collapsible-content>
                ${itemsHTML}
              </div>
            </div>
          </details>
        `;
      }
    }).join('');

    return `
      <collapsible-elements single="true">
        <div class="camp-result__accordion-wrapper">
          ${sidebarHTML}
        </div>
      </collapsible-elements>
    `;
  }

  /**
   * Navigate to specific block by index
   */
  navigateToBlock(index) {
    this.currentIndex = index;
    // TODO: Update content area and highlight current block
  }

  /**
   * Navigate to previous block
   */
  navigatePrevious() {
    if (this.currentIndex > 0) {
      this.navigateToBlock(this.currentIndex - 1);
    }
  }

  /**
   * Navigate to next block
   */
  navigateNext() {
    const campData = window.RsCampPage.getCampData();
    if (campData && this.currentIndex < campData.checklist_blocks.length - 1) {
      this.navigateToBlock(this.currentIndex + 1);
    }
  }
}


/**
 * Custom element for handling filters
 */
class RSCampResultFilters extends HTMLElement {
  constructor() {
    super();
    this.boundHandleFilterChange = this.handleFilterChange.bind(this);
  }

  connectedCallback() {
    const filterSelects = this.querySelectorAll('select');
    filterSelects.forEach(select => {
      select.addEventListener('change', this.boundHandleFilterChange);
    });
  }

  disconnectedCallback() {
    const filterSelects = this.querySelectorAll('select');
    filterSelects.forEach(select => {
      select.removeEventListener('change', this.boundHandleFilterChange);
    });
  }

  /**
   * Handle filter change and update products
   */
  async handleFilterChange() {
    const container = this.closest('.camp-result__content-products');
    if (!container) return;

    const collectionUrl = this.getAttribute('data-collection-url');
    if (!collectionUrl) return;

    // Build filter query params
    const filterParams = this.buildFilterParams();
    
    // Check if department filtering is enabled for current content
    const enableDeptFiltering = container.getAttribute('data-enable-dept-filtering') === 'true';
    
    // Load products with filters
    await window.RsCampPage.loadProducts(collectionUrl, filterParams, container, true, enableDeptFiltering);
  }

  /**
   * Build filter query parameters from select boxes
   */
  buildFilterParams() {
    const filterSelects = this.querySelectorAll('select');
    const params = [];

    filterSelects.forEach(select => {
      const name = select.getAttribute('name');
      const value = select.value;
      
      if (name && value && value.trim() !== '') {
        params.push(`${name}=${encodeURIComponent(value)}`);
      }
    });

    return params.join('&');
  }
}

/**
 * Custom element for handling reset filters
 */
class RSCampResultResetFilters extends HTMLElement {
  constructor() {
    super();
    this.boundHandleResetFilters = this.handleResetFilters.bind(this);
  }

  connectedCallback() {
    this.resetFiltersButton = this.querySelector('button');
    if (this.resetFiltersButton) {
      this.resetFiltersButton.addEventListener('click', this.boundHandleResetFilters);
    }
  }

  disconnectedCallback() {
    if (this.resetFiltersButton) {
      this.resetFiltersButton.removeEventListener('click', this.boundHandleResetFilters);
    }
  }

  /**
   * Handle reset filters button click
   */
  async handleResetFilters() {
    const container = this.closest('.camp-result__content-products');
    if (!container) return;

    const collectionUrl = this.getAttribute('data-collection-url');
    if (!collectionUrl) return;

    // Reset all filter select boxes to default value
    const filtersElement = container.querySelector('rs-camp-result-filters');
    if (filtersElement) {
      const filterSelects = filtersElement.querySelectorAll('select');
      filterSelects.forEach(select => {
        select.selectedIndex = 0;
      });
    }

    // Check if department filtering is enabled for current content
    const enableDeptFiltering = container.getAttribute('data-enable-dept-filtering') === 'true';

    // Reload products without user filters but keep department filter if enabled
    await window.RsCampPage.loadProducts(collectionUrl, '', container, !!filtersElement, enableDeptFiltering);
  }
}

/**
 * Custom element for handling pagination
 */
class RSCampResultPagination extends HTMLElement {
  constructor() {
    super();
    this.boundHandlePaginationClick = this.handlePaginationClick.bind(this);
  }

  connectedCallback() {
    const paginationLinks = this.querySelectorAll('a[data-page]');
    paginationLinks.forEach(link => {
      link.addEventListener('click', this.boundHandlePaginationClick);
    });
  }

  disconnectedCallback() {
    const paginationLinks = this.querySelectorAll('a[data-page]');
    paginationLinks.forEach(link => {
      link.removeEventListener('click', this.boundHandlePaginationClick);
    });
  }

  /**
   * Handle pagination link click
   */
  async handlePaginationClick(event) {
    event.preventDefault();
    
    const container = this.closest('.camp-result__content-products');
    if (!container) return;

    const collectionUrl = this.getAttribute('data-collection-url');
    if (!collectionUrl) return;

    const pageNumber = event.target.getAttribute('data-page');
    if (!pageNumber) return;

    // Build pagination params
    const paginationParams = `page=${pageNumber}`;
    
    // Get existing filter params if filters exist
    const filtersElement = container.querySelector('rs-camp-result-filters');
    let filterParams = '';
    if (filtersElement && typeof filtersElement.buildFilterParams === 'function') {
      filterParams = filtersElement.buildFilterParams();
    }

    // Combine filter and pagination params
    let allParams = paginationParams;
    if (filterParams) {
      allParams = `${filterParams}&${paginationParams}`;
    }

    // Check if department filtering is enabled for current content
    const enableDeptFiltering = container.getAttribute('data-enable-dept-filtering') === 'true';

    // Load products with pagination and filters
    await window.RsCampPage.loadProducts(collectionUrl, allParams, container, !!filtersElement, enableDeptFiltering);

    window.RsCampPage.scrollToResult();
  }
}

/**
 * RS Camp Result Summary. Handles summary section:
 *  - Displays summary of selected items
 */
class RSCampResultSummary extends HTMLElement {
  constructor() {
    super();

    this.viewCartButtons = document.querySelectorAll('[data-camp-summary-view-cart]');
    this.viewCartCounts = document.querySelectorAll('[data-camp-summary-view-cart-count]');
    this.cartDrawer = document.querySelector('cart-drawer');

    this.boundViewCart = this.handleViewCart.bind(this);
    this.boundCartChange = this.handleCartChange.bind(this);
  }

  connectedCallback() {
    this.viewCartButtons.forEach(button => {
      button.addEventListener('click', this.boundViewCart);
    });
    document.addEventListener('theme:cart:change', this.boundCartChange);

    // Initial cart summary update
    this.updateCartSummary();
  }
  
  disconnectedCallback() {
    this.viewCartButtons.forEach(button => {
      button.removeEventListener('click', this.boundViewCart);
    });
    document.removeEventListener('theme:cart:change', this.boundCartChange);
  }

  /**
   * Handle view cart button click
   */
  handleViewCart(event) {
    const button = event.target;
    if (!button || button.disabled) return;
    
    if (this.cartDrawer) {
      this.cartDrawer.dispatchEvent(new CustomEvent('theme:cart-drawer:show'));
    } else {
      window.location = theme.routes.cart_url;
    }
  }

  /**
   * Handle cart change event
   */
  handleCartChange() {
    this.updateCartSummary();
  }

  /**
   * Update cart summary
   */
  async updateCartSummary() {
    try {
      const cartData = await this.fetchCartData();
      const categorySummary = this.groupItemsByCategory(cartData.items);
      this.renderCartSummary(categorySummary);
    } catch (error) {
      console.error('Error updating cart summary:', error);
    }
  }

  /**
   * Group cart items by category and calculate quantities (only for current camper)
   */
  groupItemsByCategory(cartItems) {
    const categoryMap = new Map();
    const formData = window.RsCampPage.getCookie();
    const campData = window.RsCampPage.getCampData();

    if (!formData || !campData) return [];

    cartItems.forEach(item => {
      // Check if item matches current form data
      const itemCampTitle = item.properties && item.properties._CampTitle;
      const itemCamperName = item.properties && item.properties._CamperName;
      const itemCampCategory = item.properties && item.properties._CampCategory;
      
      // Only include items that match current camp and camper
      if (itemCampTitle === campData.camp_name && itemCamperName === formData.camperName && itemCampCategory) {
        if (categoryMap.has(itemCampCategory)) {
          categoryMap.set(itemCampCategory, categoryMap.get(itemCampCategory) + item.quantity);
        } else {
          categoryMap.set(itemCampCategory, item.quantity);
        }
      }
    });

    // Convert map to array of objects
    return Array.from(categoryMap.entries()).map(([category, quantity]) => ({
      category,
      quantity
    }));
  }

  /**
   * Render cart summary in the summary content area
   */
  renderCartSummary(categorySummary) {
    const summaryContent = document.querySelector('[data-camp-summary-content]');
    const summaryViewCartButtons = document.querySelectorAll('[data-camp-summary-view-cart]');
    const summaryViewCartCounts = document.querySelectorAll('[data-camp-summary-view-cart-count]');

    if (!summaryContent) return;

    if (categorySummary.length === 0) {
      const campData = window.RsCampPage.getCampData();
      summaryContent.innerHTML = campData.summary_default_content;
      
      summaryViewCartButtons.forEach(button => {
        button.disabled = true;
      });
    } else {
      const summaryHTML = categorySummary
        .map(({ category, quantity }) => `
          <div class="camp-result__summary--item">
            <span class="camp-result__summary--quantity">${quantity}</span>
            <span class="camp-result__summary--symbol">x</span>
            <span class="camp-result__summary--category">${category}</span>      
          </div>
        `)
        .join('');

      summaryContent.innerHTML = summaryHTML;
      
      summaryViewCartButtons.forEach(button => {
        button.disabled = false;
      });
      
      // Add scroll indicator if content exceeds height
      this.addScrollIndicator(summaryContent);
    }

    const totalQuantity = categorySummary.reduce((acc, { quantity }) => acc + quantity, 0);
    summaryViewCartCounts.forEach(countElement => {
      countElement.innerHTML = `(${totalQuantity})`;
    });
  }

  /**
   * Add scroll indicator if content exceeds height limit
   */
  addScrollIndicator(summaryContent) {
    // Remove existing scroll indicator
    const existingIndicator = summaryContent.querySelector('.camp-result__summary--scroll-indicator');
    if (existingIndicator) {
      existingIndicator.remove();
    }
    
    // Check if content height exceeds the limit (200px)
    if (summaryContent.scrollHeight > 200) {
      const scrollIndicator = document.createElement('div');
      scrollIndicator.className = 'camp-result__summary--scroll-indicator is-active';
      scrollIndicator.innerHTML = `
        <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-arrow-up" viewBox="0 0 24 24"><path d="M12.25 18.5V6m0 0 6 6m-6-6-6 6" stroke="#000" stroke-linecap="round" stroke-linejoin="round"/></svg>
        <span>Scroll for more items</span>`;
    
      summaryContent.appendChild(scrollIndicator);
      
      const handleScroll = () => {
        if (summaryContent.scrollTop > 10) {
          scrollIndicator.classList.remove('is-active');
        } else {
          scrollIndicator.classList.add('is-active');
        }
      };
      
      summaryContent.addEventListener('scroll', handleScroll);
      scrollIndicator._handleScroll = handleScroll;
    }
  }

  /**
   * Fetch cart data from Shopify
   */
  async fetchCartData() {
    const response = await fetch('/cart.js');
    if (!response.ok) {
      throw new Error(`Failed to fetch cart data: ${response.status}`);
    }
    return response.json();
  }
}

/**
 * REGISTER CUSTOM ELEMENTS
 */
if (!customElements.get('rs-camp-form')) {
  customElements.define('rs-camp-form', RSCampForm);
}
if (!customElements.get('rs-camp-result')) {
  customElements.define('rs-camp-result', RSCampResult);
}
if (!customElements.get('rs-camp-result-filters')) {
  customElements.define('rs-camp-result-filters', RSCampResultFilters);
}
if (!customElements.get('rs-camp-result-reset-filters')) {
  customElements.define('rs-camp-result-reset-filters', RSCampResultResetFilters);
}
if (!customElements.get('rs-camp-result-pagination')) {
  customElements.define('rs-camp-result-pagination', RSCampResultPagination);
}
if (!customElements.get('rs-camp-result-summary')) {
  customElements.define('rs-camp-result-summary', RSCampResultSummary);
}
if (!customElements.get('rs-camp-result-reset-filters')) {
  customElements.define('rs-camp-result-reset-filters', RSCampResultResetFilters);
}